<script setup lang="ts">
import { ref } from 'vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon, BarsArrowUpIcon, BarsArrowDownIcon } from '@heroicons/vue/20/solid'
import { claimClaimStatesByType } from '@/utils/config/constants/claim-state';
import { claimLegalStepById } from '@/utils/config/constants/claims-legal-steps';
import BadgeLegalStep from '@/components/ui/badges/BadgeLegalStep.vue';
import BadgeClaimType from '@/components/ui/badges/BadgeClaimType.vue';
import DotLegalStep from '@/components/ui/badges/DotLegalStep.vue';

const sortBy = ref([{value: 'claim'}, {value: 'claim_claimStateRemainingDelay_desc'},
  {value: 'claim_claimStateRemainingDelay_asc'}
]);

function getSortRefinement(currentRefinementExpiration) {
  // Not selected
  if (currentRefinementExpiration !== 'claim_claimStateRemainingDelay_desc'
      && currentRefinementExpiration !== 'claim_claimStateRemainingDelay_asc') {
    return 'claim_claimStateRemainingDelay_desc';
  } else if (currentRefinementExpiration === 'claim_claimStateRemainingDelay_desc') {
    return 'claim_claimStateRemainingDelay_asc';
  }

  return 'claim';
}

function transformItems(items) {
  const mappedItems = items.map(item => ({
    ...item,
    claimState: claimClaimStatesByType(item.value),
    legalStepId: claimClaimStatesByType(item.value)?.legal_step_id
  }));

  return mappedItems.sort((a, b) => a.legalStepId - b.legalStepId)
}
</script>
<template>
  <div class="inline-flex rounded-md shadow-sm">
    <ais-sort-by :items="sortBy">
      <template v-slot="{ refine: refineExpiration, currentRefinement: currentRefinementExpiration }">
        <button type="button"
                v-tooltip.bottom="'Trier par délai restant'"
                @click="refineExpiration(getSortRefinement(currentRefinementExpiration))"
                class="relative inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white rounded-l-md ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
          Étape juridique
          <span
              v-if="currentRefinementExpiration === 'claim_claimStateRemainingDelay_desc' || currentRefinementExpiration === 'claim_claimStateRemainingDelay_asc'"
              class="flex-none ml-2 text-gray-900 bg-gray-100 rounded cursor-pointer group-hover:bg-gray-200">
            <BarsArrowUpIcon v-show="currentRefinementExpiration === 'claim_claimStateRemainingDelay_desc'"
                             class="w-5 h-5" aria-hidden="true"/>
            <BarsArrowDownIcon v-show="currentRefinementExpiration === 'claim_claimStateRemainingDelay_asc'"
                               class="w-5 h-5" aria-hidden="true"/>
          </span>
        </button>
      </template>
    </ais-sort-by>

    <Menu as="div" class="relative block -ml-px">
      <MenuButton
          class="relative inline-flex items-center px-2 py-2 text-gray-400 bg-white rounded-r-md ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
        <span class="sr-only">Open options</span>
        <ChevronDownIcon class="w-5 h-5" aria-hidden="true"/>
      </MenuButton>
      <transition enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0"
                  enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in"
                  leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
        <MenuItems :unmount="false"
                   class="absolute right-0 z-10 mt-2 overflow-y-auto origin-top-right bg-white rounded-md shadow-lg w-80 max-h-96 ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div class="py-2">

            <MenuItem v-slot="{ active }">
              <div>
                <div class="block">
                  <div class="py-2 ml-4">Étape</div>
                  <ais-refinement-list attribute="claimLegalStepName" operator="or">
                    <template v-slot="{ items: legalSteps, isFromSearch, refine }">
                      <div class="block">
                        <ul>
                          <li v-if="isFromSearch && !legalSteps.length">No results.</li>
                          <li v-for="legalStep in legalSteps" :key="legalStep.value" class="divide-y divide-gray-200">
                            <div @click="refine(legalStep.value)"
                                 class="flex px-4 py-1 text-right cursor-pointer whitespace-nowrap hover:bg-blue-50">
                              <input type="checkbox" :checked="legalStep.isRefined"
                                     class="w-4 h-4 mt-1 mr-2 text-blue-600 border-gray-300 rounded cursor-pointer focus:ring-blue-600" />
                              <BadgeClaimType :name="legalStep.value" />
                              <BadgeLegalStep :name="(legalStep.value as any)" :dot="true"
                                              :additional-text="` - (${legalStep.count || 0})`" />
                            </div>
                          </li>
                        </ul>
                      </div>

                    </template>
                  </ais-refinement-list>
                </div>
                <div class="mt-4 border-t-2 border-grey-100" v-permission="['UPCLAIM_ADMIN', 'LAWYER']">
                  <div class="py-2 mt-2 ml-4">État</div>
                  <ais-refinement-list attribute="claimStateType" operator="or" :transform-items="transformItems">
                    <template v-slot="{ items: states, refine: refineState, createURL }">
                      <div v-for="state in states" :key="state.value" @click="refineState(state.value)"
                           class="flex px-4 text-xs font-normal cursor-pointer hover:bg-blue-50">
                        <div class="py-2">
                          <input :checked="state.isRefined" type="checkbox"
                                 class="w-4 h-4 text-blue-600 border-gray-300 rounded cursor-pointer focus:ring-blue-600"/>
                        </div>
                        <!-- <div class="py-2.5 ml-2">
                          <DotLegalStep :legal-step-name="claimLegalStepById(claimClaimStatesByType(state.value).legal_step_id)" />
                        </div> -->
                          <span class="py-2 ml-2">
                            {{ claimClaimStatesByType(state.value).display_name }} ({{ state.count }})
                          </span>
                        <div class="py-2.5 ml-2">
                          <DotLegalStep :legal-step-name="claimLegalStepById(claimClaimStatesByType(state.value)?.legal_step_id)" />
                        </div>
                      </div>
                    </template>
                  </ais-refinement-list>
                </div>
              </div>
            </MenuItem>
          </div>
        </MenuItems>
      </transition>
    </Menu>

  </div>
</template>
